import React, { useState, useEffect } from 'react';
import { useReactMediaRecorder } from 'react-media-recorder';
import { Mic, Square, ArrowLeft, Copy, Check, Loader, FileText, Image } from 'lucide-react';
import { PostPrompt } from '../types';
import { transcribeAudio, generateThoughtLeadershipPost, saveRecording, savePost } from '../services/audioService';
import { useSupabaseUser } from '../hooks/useSupabaseUser';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../hooks/useAppContext';
import ImageModal from './ImageModal';
import AnalysisSummary from './AnalysisSummary';

interface ContentDisplay {
  type: 'image' | 'text';
  content: string;
  url?: string;
}

export default function ContentRecorder() {
  const navigate = useNavigate();
  const { user } = useSupabaseUser();
  const { 
    imageAnalysis, 
    currentQuestionIndex,
    setCurrentQuestionIndex,
    setFormattedPosts 
  } = useAppContext();

  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [generatedPost, setGeneratedPost] = useState<string | null>(null);
  const [processingStep, setProcessingStep] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editedPost, setEditedPost] = useState<string>('');
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied'>('idle');
  const [contentToDisplay, setContentToDisplay] = useState<ContentDisplay | null>(null);

  if (!imageAnalysis) {
    return (
      <div className="max-w-4xl mx-auto p-4">
        <p className="text-gray-600">No image analysis available. Please upload an image first.</p>
      </div>
    );
  }

  if (!imageAnalysis.id) {
    return (
      <div className="max-w-4xl mx-auto p-4">
        <p className="text-gray-600">Image analysis is missing required data. Please try uploading the image again.</p>
      </div>
    );
  }

  const prompts = imageAnalysis.questions;
  const currentIndex = currentQuestionIndex;
  const totalQuestions = prompts.length;
  const currentPrompt = prompts[currentIndex];

  const [recordingStatus, setRecordingStatus] = useState<{
    status: 'idle' | 'recording' | 'processing' | 'complete' | 'error';
    duration?: number;
    recordingId?: string;
  }>({
    status: 'idle',
    duration: 0,
  });

  const {
    status,
    startRecording,
    stopRecording,
    mediaBlobUrl,
    clearBlobUrl,
  } = useReactMediaRecorder({
    audio: true,
    onStart: () => {
      console.log('Recording started');
    },
    onStop: async (blobUrl, blob) => {
      console.log('Recording stopped', { blobUrl, blobSize: blob.size });
      handleRecordingComplete(blob);
    },
    onError: (error) => {
      console.error('Recording error:', error);
    }
  });

  const handleRecordingComplete = async (blob: Blob) => {
    if (!user?.id) {
      console.error('Recording failed: No user ID');
      setError('User not authenticated');
      return;
    }

    try {
      setRecordingStatus(prev => ({ ...prev, status: 'processing' }));
      setError(null);
      setGeneratedPost(null);

      // Step 1: Save the recording
      setProcessingStep('Saving recording...');
      const recording = await saveRecording(blob, user.id);

      // Step 2: Transcribe the audio
      setProcessingStep('Transcribing audio...');
      const transcript = await transcribeAudio(blob);
      
      // Step 3: Generate the post
      setProcessingStep('Generating LinkedIn post...');
      const postPrompt: PostPrompt = {
        type: imageAnalysis.imageUrl ? 'image' : 'text',
        content: imageAnalysis.text || '',
        url: imageAnalysis.imageUrl,
        question: currentPrompt,
        context: imageAnalysis.context
      };

      const generatedPost = await generateThoughtLeadershipPost(
        transcript.text,
        postPrompt
      );

      // Step 4: Save the post
      setProcessingStep('Saving post...');
      const savedPost = await savePost(recording.id, generatedPost, imageAnalysis.id);

      setGeneratedPost(generatedPost);
      setRecordingStatus({ status: 'complete', recordingId: recording.id });
      setProcessingStep(null);

    } catch (error) {
      console.error('Recording process failed:', {
        error,
        step: processingStep,
        status: recordingStatus.status
      });
      setError(error instanceof Error ? error.message : 'An error occurred');
      setRecordingStatus({ status: 'error' });
      setProcessingStep(null);
    }
  };

  const handleStartRecording = () => {
    setRecordingStatus({ status: 'recording', duration: 0 });
    setGeneratedPost(null);
    setError(null);
    startRecording();
  };

  const handleStopRecording = () => {
    setRecordingStatus(prev => ({ ...prev, status: 'processing' }));
    stopRecording();
  };

  const handleBack = () => {
    if (currentIndex > 0) {
      setCurrentQuestionIndex(currentIndex - 1);
    } else {
      navigate('/questions');
    }
  };

  const handleNext = () => {
    if (currentIndex < totalQuestions - 1) {
      setCurrentQuestionIndex(currentIndex + 1);
      setRecordingStatus({ status: 'idle', duration: 0 });
      setGeneratedPost(null);
      setError(null);
    } else {
      navigate('/history');
    }
  };

  const extractQuestionType = (prompt: string): string => {
    const match = prompt.match(/^([^:]+):/);
    return match ? match[1] : 'Question';
  };

  const formatQuestion = (prompt: string): string => {
    return prompt.replace(/^[^:]+:\s*/, '').trim();
  };

  // Format analysis text with line breaks after each sentence
  const formattedAnalysis = imageAnalysis.analysis.replace(/\.\s+/g, '.\n\n');

  useEffect(() => {
    if (generatedPost) {
      setEditedPost(generatedPost);
    }
  }, [generatedPost]);

  useEffect(() => {
    if (imageAnalysis) {
      console.log('ImageAnalysis received:', {
        hasImageUrl: !!imageAnalysis.imageUrl,
        hasText: !!imageAnalysis.text,
        analysisId: imageAnalysis.id,
        questionsCount: imageAnalysis.questions.length,
        context: imageAnalysis.context
      });

      setContentToDisplay({
        type: imageAnalysis.imageUrl ? 'image' : 'text',
        content: imageAnalysis.text || '',
        url: imageAnalysis.imageUrl
      });

      console.log('Content display set to:', {
        type: imageAnalysis.imageUrl ? 'image' : 'text',
        hasContent: !!imageAnalysis.text,
        hasUrl: !!imageAnalysis.imageUrl
      });
    }
  }, [imageAnalysis]);

  const handleCopyPost = async () => {
    try {
      await navigator.clipboard.writeText(editedPost);
      setCopyStatus('copied');
      setTimeout(() => setCopyStatus('idle'), 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="p-6">

          {/* Selected Question */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Selected Question</h3>
            <div className="bg-blue-50 rounded-lg p-4">
              <p className="text-blue-900">
                {imageAnalysis?.questions[currentQuestionIndex || 0]}
              </p>
            </div>
          </div>

          {/* Recording Section */}
          <div className="mb-12 bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-8">
            <div className="flex items-center justify-between mb-6">
              <button
                onClick={handleBack}
                className="flex items-center text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </button>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setCurrentQuestionIndex(currentIndex - 1)}
                  disabled={currentIndex === 0}
                  className="p-2 rounded-lg hover:bg-white/50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ArrowLeft className="w-4 h-4 text-gray-600" />
                </button>
                <div className="text-sm font-medium text-blue-600">
                  Question {currentIndex + 1} of {totalQuestions}
                </div>
                <button
                  onClick={() => setCurrentQuestionIndex(currentIndex + 1)}
                  disabled={currentIndex === totalQuestions - 1}
                  className="p-2 rounded-lg hover:bg-white/50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ArrowLeft className="w-4 h-4 text-gray-600" />
                </button>
              </div>
            </div>

            <div className="bg-white/80 rounded-xl p-6 mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-2">Record Your Response</h2>
              <div className="flex items-center gap-2 mb-4">
                <span className="px-3 py-1 text-sm font-medium text-purple-700 bg-purple-100 rounded-full">
                  {extractQuestionType(currentPrompt)}
                </span>
              </div>
              <p className="text-gray-600">{formatQuestion(currentPrompt)}</p>
            </div>

            <div className="bg-white/80 rounded-xl p-6 space-y-6">
              <div className="flex justify-center">
                {recordingStatus.status === 'idle' ? (
                  <button
                    onClick={handleStartRecording}
                    className="flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    <Mic className="w-5 h-5 mr-2" />
                    Start Recording
                  </button>
                ) : recordingStatus.status === 'recording' ? (
                  <button
                    onClick={handleStopRecording}
                    className="flex items-center px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                  >
                    <Square className="w-5 h-5 mr-2" />
                    Stop Recording
                  </button>
                ) : recordingStatus.status === 'processing' ? (
                  <div className="text-center">
                    <div className="flex items-center justify-center text-gray-600 mb-2">
                      <Loader className="w-5 h-5 animate-spin mr-3" />
                      {processingStep}
                    </div>
                    <p className="text-sm text-gray-500">Please wait while we process your recording...</p>
                  </div>
                ) : null}
              </div>

              {error && (
                <div className="p-4 bg-red-50 border border-red-100 rounded-lg">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              {generatedPost && (
                <div className="space-y-4">
                  <div className="p-6 bg-green-50 border border-green-100 rounded-lg">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-semibold text-green-800">Generated LinkedIn Post</h3>
                      <button
                        onClick={handleCopyPost}
                        className="flex items-center px-3 py-1.5 text-sm bg-white text-green-700 rounded-lg hover:bg-green-50 border border-green-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors"
                      >
                        {copyStatus === 'copied' ? (
                          <>
                            <Check className="w-4 h-4 mr-1.5" />
                            Copied!
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4 mr-1.5" />
                            Copy
                          </>
                        )}
                      </button>
                    </div>
                    <textarea
                      value={editedPost}
                      onChange={(e) => setEditedPost(e.target.value)}
                      className="w-full min-h-[300px] p-4 text-green-700 bg-white/50 rounded-lg border border-green-200 focus:ring-2 focus:ring-green-500 focus:border-transparent resize-y"
                      placeholder="Your LinkedIn post content..."
                    />
                  </div>

                  <div className="flex justify-end">
                    <button
                      onClick={handleNext}
                      className="flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    >
                      {currentIndex < totalQuestions - 1 ? 'Next Question' : 'View All Posts'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Tips Section */}
          <div className="bg-white/80 rounded-xl p-6 mt-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Tips for Great Responses</h2>
            <div className="grid gap-4 md:grid-cols-3">
              <div className="bg-gradient-to-r from-blue-50 to-blue-100/50 rounded-xl p-6">
                <div className="text-blue-600 font-bold text-lg mb-2">1. Be Authentic</div>
                <p className="text-gray-600">Share real experiences and insights from your professional journey.</p>
              </div>
              <div className="bg-gradient-to-r from-purple-50 to-purple-100/50 rounded-xl p-6">
                <div className="text-purple-600 font-bold text-lg mb-2">2. Be Specific</div>
                <p className="text-gray-600">Use concrete examples and data points to support your arguments.</p>
              </div>
              <div className="bg-gradient-to-r from-indigo-50 to-indigo-100/50 rounded-xl p-6">
                <div className="text-indigo-600 font-bold text-lg mb-2">3. Be Engaging</div>
                <p className="text-gray-600">Start with a hook and end with a thought-provoking conclusion.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Analysis Summary */}
      <AnalysisSummary 
        imageAnalysis={imageAnalysis} 
        onOpenImage={() => setIsImageModalOpen(true)} 
      />

      {/* Image Modal */}
      <ImageModal 
        imageUrl={imageAnalysis.imageUrl} 
        isOpen={isImageModalOpen} 
        onClose={() => setIsImageModalOpen(false)} 
      />
    </div>
  );
}